<template>
  <div class="book-panel" data-testid="book-panel">
    <header class="book-panel__header">
      <h1 class="book-panel__title">{{ title }}</h1>
    </header>

    <div class="book-panel__card">
      <div v-if="!isAccountEmpty" class="book-panel__filters" data-testid="book-filters">
        <div class="book-panel__filter">
          <label class="book-panel__filter-label" for="book-panel-status-filter">Status</label>
          <select
            id="book-panel-status-filter"
            v-model="statusModel"
            class="book-panel__select"
            data-testid="book-status-filter"
          >
            <option v-for="option in STATUS_OPTIONS" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="book-panel__filter">
          <label class="book-panel__filter-label" for="book-panel-sort">Sort by</label>
          <select
            id="book-panel-sort"
            v-model="orderingModel"
            class="book-panel__select"
            data-testid="book-sort"
          >
            <option v-for="option in SORT_OPTIONS" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <Button
          :icon="'add-icon.svg'"
          :normal="'normal-active'"
          :label="'Add Book'"
          :must="false"
          class="book-panel__add-button"
          @onClick="$emit('add-book')"
        />
      </div>

      <div v-if="loading" class="book-panel__loading" role="status" aria-live="polite">
        <span class="book-panel__visually-hidden">Loading books</span>
        <div v-for="n in 3" :key="n" class="book-panel__skeleton-row" aria-hidden="true">
          <span class="book-panel__skeleton-bar book-panel__skeleton-bar--wide" />
          <span class="book-panel__skeleton-bar" />
          <span class="book-panel__skeleton-bar" />
          <span class="book-panel__skeleton-bar" />
          <span class="book-panel__skeleton-bar" />
          <span class="book-panel__skeleton-bar" />
          <span class="book-panel__skeleton-bar book-panel__skeleton-bar--icon" />
        </div>
      </div>

      <div v-else-if="rows.length > 0" class="book-panel__table-wrap">
        <table class="book-panel__table" data-testid="book-table">
          <caption class="book-panel__visually-hidden">
            Procedure books
          </caption>
          <colgroup>
            <col class="book-panel__col-name" />
            <col class="book-panel__col-count" />
            <col class="book-panel__col-count" />
            <col class="book-panel__col-date" />
            <col class="book-panel__col-date" />
            <col class="book-panel__col-status" />
            <col class="book-panel__col-menu" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" class="book-panel__th">Name</th>
              <th scope="col" class="book-panel__th">Chapters</th>
              <th scope="col" class="book-panel__th">Procedures</th>
              <th scope="col" class="book-panel__th">Created</th>
              <th scope="col" class="book-panel__th">Last updated</th>
              <th scope="col" class="book-panel__th">Status</th>
              <th scope="col" class="book-panel__th">
                <span class="book-panel__visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.key"
              class="book-panel__row"
              data-testid="book-row"
              @click="$emit('open-book', row.book)"
            >
              <td class="book-panel__cell">
                <button
                  type="button"
                  class="book-panel__name-btn"
                  :aria-label="`Open ${row.name}`"
                  data-testid="book-name"
                >
                  <span class="book-panel__name">{{ row.name }}</span>
                  <span v-if="row.subtext" class="book-panel__subtext">{{ row.subtext }}</span>
                </button>
              </td>
              <td class="book-panel__cell book-panel__cell--muted" data-testid="book-chapters">
                {{ row.chapters }}
              </td>
              <td class="book-panel__cell book-panel__cell--muted" data-testid="book-procedures">
                {{ row.procedures }}
              </td>
              <td class="book-panel__cell book-panel__cell--muted" data-testid="book-created">
                {{ row.created }}
              </td>
              <td class="book-panel__cell book-panel__cell--muted" data-testid="book-updated">
                {{ row.updated }}
              </td>
              <td class="book-panel__cell">
                <OrgObjectStatusBadge
                  v-if="row.badgeStatus"
                  :status="row.badgeStatus"
                  :label="row.badgeLabel"
                  data-testid="book-status-badge"
                />
              </td>
              <td class="book-panel__cell book-panel__cell--menu" @click.stop>
                <MenuIcon
                  :dropdownItems="MENU_ITEMS"
                  :menuWidth="true"
                  :showTooltip="false"
                  @onClick="onMenuClick($event, row.book)"
                >
                  <template #activator>
                    <button
                      type="button"
                      class="book-panel__menu-btn"
                      :aria-label="`Actions for ${row.name}`"
                      data-testid="book-actions"
                    >
                      <img :src="dotsIconSrc" alt="" width="24" height="24" aria-hidden="true" />
                    </button>
                  </template>
                </MenuIcon>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="empty-state" role="status" data-testid="book-empty-state">
        <div class="background-pattern">
          <img
            :src="require('@components/assets/bookstack/background-pattern.svg')"
            alt=""
            class="pattern-image"
          />
        </div>
        <div class="empty-content">
          <div class="icon-wrap">
            <Icon name="book_stack_icon" size="large" />
          </div>
          <div class="text-content">
            <h3 class="empty-title">{{ emptyTitle }}</h3>
            <p class="empty-description">{{ emptyDescription }}</p>
          </div>
          <Button
            v-if="isAccountEmpty"
            :icon="'add-icon.svg'"
            :normal="'normal-active'"
            :label="'Add Book'"
            :must="false"
            data-testid="book-empty-add"
            @onClick="$emit('add-book')"
          />
        </div>
      </div>

      <ExPagination
        v-if="showPagination"
        class="book-panel__pagination"
        :pagination="paginationForEx"
        @onRequest="onPaginationRequest"
      />
    </div>
  </div>
</template>

<script setup>
/**
 * BookPanel Component
 * Procedure Books home: filters bar (status, sort, Add Book) above a table of books.
 */
import { computed } from "vue";
import Button from "@components/buttons/Button.vue";
import Icon from "@components/icons/Icon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";

const dotsIconSrc = require("@components/assets/job-family/dots-horizontal.svg");

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "published", label: "Published" },
  { value: "unpublished", label: "Unpublished" },
];

const SORT_OPTIONS = [
  { value: "name", label: "Name (A-Z)" },
  { value: "-name", label: "Name (Z-A)" },
  { value: "-updated_on", label: "Recently updated" },
  { value: "updated_on", label: "Least recently updated" },
  { value: "-created_on", label: "Newest" },
  { value: "created_on", label: "Oldest" },
];

const MENU_ITEMS = [{ name: "Open", value: "open" }];

const BADGES = {
  published: { status: "active", label: "Published" },
  unpublished: { status: "draft", label: "Unpublished" },
};

const props = defineProps({
  title: {
    type: String,
    default: "Procedure Books",
  },
  books: {
    type: Array,
    default: () => [],
  },
  searchQuery: {
    type: String,
    default: "",
  },
  statusFilter: {
    type: String,
    default: "",
  },
  ordering: {
    type: String,
    default: "name",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 20,
  },
});

const emit = defineEmits([
  "add-book",
  "open-book",
  "update:status-filter",
  "update:ordering",
  "page-change",
]);

const statusModel = computed({
  get: () => props.statusFilter,
  set: (value) => emit("update:status-filter", value),
});

const orderingModel = computed({
  get: () => props.ordering,
  set: (value) => emit("update:ordering", value),
});

const countOf = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const rows = computed(() =>
  props.books.map((book, index) => {
    const badge = BADGES[String(book?.status ?? "").toLowerCase()];
    return {
      book,
      key: book?.id ?? `${book?.name}-${index}`,
      name: book?.name ?? "",
      subtext: book?.subtext ?? "",
      chapters: countOf(book?.chapters_count),
      procedures: countOf(book?.procedures_count),
      created: book?.created_on || "-",
      updated: book?.updated_on || "-",
      badgeStatus: badge?.status ?? "",
      badgeLabel: badge?.label ?? "",
    };
  })
);

const hasActiveFilters = computed(
  () => props.searchQuery.trim() !== "" || props.statusFilter !== ""
);

const isAccountEmpty = computed(
  () => !props.loading && props.books.length === 0 && !hasActiveFilters.value
);

const emptyTitle = computed(() => (isAccountEmpty.value ? "Do not exist" : "None found"));
const emptyDescription = computed(() =>
  isAccountEmpty.value ? "Add a book to store Procedures" : "Try changing your search or filters."
);

const showPagination = computed(() => !props.loading && props.totalCount > props.itemsPerPage);

const paginationForEx = computed(() => ({
  page: props.currentPage,
  total: props.totalCount,
  perPage: props.itemsPerPage,
}));

const onPaginationRequest = ({ page }) => {
  emit("page-change", page);
};

const onMenuClick = (item, book) => {
  if (item?.value === "open") {
    emit("open-book", book);
  }
};
</script>

<style scoped>
.book-panel {
  padding: 32px 32px 40px 32px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.book-panel__header {
  display: flex;
  align-items: center;
}

.book-panel__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 38px;
  color: #101828;
}

.book-panel__card {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.book-panel__filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #eaecf0;
}

.book-panel__filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.book-panel__filter-label {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.book-panel__select {
  min-width: 200px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.book-panel__select:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.book-panel__add-button {
  width: 168px;
  min-width: 168px;
  margin-left: auto;
}

.book-panel__table-wrap {
  width: 100%;
  overflow-x: auto;
}

.book-panel__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.book-panel__col-name {
  width: auto;
}

.book-panel__col-count {
  width: 120px;
}

.book-panel__col-date {
  width: 140px;
}

.book-panel__col-status {
  width: 140px;
}

.book-panel__col-menu {
  width: 72px;
}

.book-panel__th {
  height: 44px;
  padding: 12px 24px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  text-align: left;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.book-panel__row {
  cursor: pointer;
}

.book-panel__row:hover .book-panel__cell {
  background: #f9fafb;
}

.book-panel__cell {
  height: 72px;
  padding: 0 24px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #101828;
}

.book-panel__cell--muted {
  color: #475467;
}

.book-panel__cell--menu {
  padding: 0 16px;
  text-align: center;
}

.book-panel__name-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.book-panel__name-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.book-panel__name,
.book-panel__subtext {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.book-panel__name {
  font-weight: 500;
  color: #101828;
}

.book-panel__subtext {
  color: #475467;
}

.book-panel__menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.book-panel__menu-btn:hover {
  background: #f2edff;
}

.book-panel__menu-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.book-panel__loading {
  padding: 8px 24px;
}

.book-panel__skeleton-row {
  display: grid;
  grid-template-columns: 2.2fr 0.7fr 0.7fr 0.9fr 0.9fr 0.8fr 0.4fr;
  gap: 16px;
  align-items: center;
  height: 72px;
  border-bottom: 1px solid #eaecf0;
}

.book-panel__skeleton-row:last-child {
  border-bottom: none;
}

.book-panel__skeleton-bar {
  display: block;
  width: 60%;
  height: 14px;
  border-radius: 9999px;
  background: #f2f4f7;
}

.book-panel__skeleton-bar--wide {
  width: 70%;
}

.book-panel__skeleton-bar--icon {
  width: 24px;
  height: 24px;
  justify-self: center;
}

.book-panel__pagination {
  border-top: 1px solid #eaecf0;
}

.book-panel__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 960px) {
  .book-panel {
    padding: 24px 16px 32px 16px;
  }

  .book-panel__title {
    font-size: 24px;
    line-height: 32px;
  }

  .book-panel__add-button {
    margin-left: 0;
  }
}

/* Empty State Styles */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  position: relative;
  width: 100%;
  padding: 40px 20px;
}

.background-pattern {
  position: absolute;
  width: 480px;
  height: 480px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% - 70px));
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pattern-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 512px;
  width: 100%;
  z-index: 1;
}

.icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 108px;
  height: 108px;
  padding: 32px;
  border-radius: 200px;
  background: linear-gradient(180deg, #f9fafb 0%, #edf0f3 100%);
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  text-align: center;
  max-width: 352px;
  width: 100%;
}

.empty-title {
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
  margin: 0;
}

.empty-description {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  margin: 0;
}

@media (max-width: 600px) {
  .empty-state {
    min-height: 300px;
    padding: 20px;
  }

  .background-pattern {
    width: 320px;
    height: 320px;
    top: -100px;
  }

  .icon-wrap {
    width: 88px;
    height: 88px;
    padding: 24px;
  }
}
</style>
